<script setup lang="ts">
  import { useTranslation } from '@/composables/useTranslation'
  import { List } from 'lucide-vue-next'
  import { computed } from 'vue'

  const props = defineProps<{
    firstName?: string | null
    lastName?: string | null
    /** Figma: show the fixed “Create document” quick action when true. */
    showCreateDocumentQuickAction?: boolean
    /** Figma: show the fixed “To-do from case” quick action when true. */
    showTodoFromCaseQuickAction?: boolean
    /** Figma: show the “Show all” / prompt library entry when true. */
    showShowAllQuickAction?: boolean
    /** Prompt text sent for the create-document chip (matches `fullscreenTopTemplates[0]`). */
    createDocumentQuickActionPrompt?: string
    /** Prompt text sent for the to-do chip (matches `fullscreenTopTemplates[1]`). */
    todoFromCaseQuickActionPrompt?: string
  }>()

  const emit = defineEmits<{
    (e: 'sendSuggestion', prompt: string): void
    (e: 'showAll'): void
  }>()

  const { t } = useTranslation({
    en: {
      hello: 'Hello',
      helloGeneric: 'Hello,',
      howCanIHelp: 'how can I help you?',
      createDocument: 'Create document',
      todoFromCase: 'Generate to-do list from case',
      showAll: 'Show all'
    },
    de: {
      hello: 'Hallo',
      helloGeneric: 'Hallo,',
      howCanIHelp: 'was möchten Sie erledigen?',
      createDocument: 'Dokument erstellen',
      todoFromCase: 'To-do-Liste aus Akte generieren',
      showAll: 'Alle anzeigen'
    },
    fr: {
      hello: 'Bonjour',
      helloGeneric: 'Bonjour,',
      howCanIHelp: 'que souhaitez-vous faire ?',
      createDocument: 'Créer un document',
      todoFromCase: 'Générer une liste de tâches à partir du dossier',
      showAll: 'Tout afficher'
    }
  })

  const displayName = computed(() =>
    [props.firstName?.trim(), props.lastName?.trim()].filter(Boolean).join(' ')
  )

  const helloPrefix = computed(() => (displayName.value ? t('hello') : t('helloGeneric')))
</script>

<template>
  <div
    class="flex w-full shrink-0 flex-col items-center"
    data-cy="fullscreen-welcome"
  >
    <!-- Figma 51916 / 51917: salutation + gradient headline -->
    <div class="flex w-full max-w-md flex-col items-center text-center">
      <p
        class="w-full text-2xl font-semibold leading-8 tracking-normal text-[var(--neutral-300-inactive,#D1D5DB)]
          font-inter dark:text-neutral-500"
      >
        <template v-if="displayName">
          {{ helloPrefix }}
          {{ displayName }},
        </template>
        <template v-else>
          {{ helloPrefix }}
        </template>
      </p>
      <p
        class="w-full bg-gradient-to-r from-[#EF469A] to-[#9333EA] bg-clip-text text-2xl font-semibold leading-8
          tracking-normal text-transparent font-inter dark:from-[#F472B6] dark:to-[#A78BFA]"
      >
        {{ t('howCanIHelp') }}
      </p>
    </div>

    <!-- Figma 51918–51921: quick actions + Alle anzeigen -->
    <div class="mt-8 flex w-full max-w-4xl flex-col items-center gap-4 sm:mt-10">
      <div
        class="flex w-full flex-wrap items-center justify-center gap-2.5 md:gap-2.5"
        data-cy="fullscreen-welcome-suggestions-row"
      >
        <button
          v-if="showCreateDocumentQuickAction"
          type="button"
          class="box-border inline-flex max-w-full shrink-0 items-center gap-2.5 rounded-lg
            bg-[var(--Primary-50,#FAF5FF)] px-2 py-1.5 text-left transition font-inter hover:bg-purple-100/80
            focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2
            focus-visible:outline-purple-600 dark:bg-neutral-800/90 dark:hover:bg-neutral-800"
          data-cy="fullscreen-welcome-suggestion"
          @click="emit('sendSuggestion', props.createDocumentQuickActionPrompt ?? '')"
        >
          <span
            class="line-clamp-1 min-h-5 min-w-0 flex-1 overflow-hidden text-ellipsis text-sm font-normal leading-5
              text-[var(--neutral-800-main-text,#1F2937)] dark:text-neutral-100"
          >
            {{ t('createDocument') }}
          </span>
        </button>

        <button
          v-if="showTodoFromCaseQuickAction"
          type="button"
          class="box-border inline-flex max-w-full shrink-0 items-center gap-2.5 rounded-lg
            bg-[var(--Primary-50,#FAF5FF)] px-2 py-1.5 text-left transition font-inter hover:bg-purple-100/80
            focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2
            focus-visible:outline-purple-600 dark:bg-neutral-800/90 dark:hover:bg-neutral-800"
          data-cy="fullscreen-welcome-suggestion"
          @click="emit('sendSuggestion', props.todoFromCaseQuickActionPrompt ?? '')"
        >
          <List
            class="size-5 shrink-0 text-[var(--neutral-800-main-text,#1F2937)] dark:text-neutral-200"
            :stroke-width="1.25"
          />
          <span
            class="line-clamp-1 min-h-5 min-w-0 flex-1 overflow-hidden text-ellipsis text-sm font-normal leading-5
              text-[var(--neutral-800-main-text,#1F2937)] dark:text-neutral-100"
          >
            {{ t('todoFromCase') }}
          </span>
        </button>

        <button
          v-if="showShowAllQuickAction"
          type="button"
          class="box-border inline-flex max-w-full shrink-0 items-center gap-2.5 rounded-lg
            bg-[var(--Primary-50,#FAF5FF)] px-2 py-1.5 text-left transition font-inter hover:bg-purple-100/80
            focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2
            focus-visible:outline-purple-600 dark:bg-neutral-800/90 dark:hover:bg-neutral-800"
          data-cy="fullscreen-welcome-show-all"
          @click="emit('showAll')"
        >
          <svg
            class="size-5 shrink-0 text-[var(--neutral-800-main-text,#1F2937)] dark:text-neutral-200"
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M10.0003 1.66675C11.6171 1.66645 13.1991 2.13648 14.5534 3.01955C15.9077 3.90261 16.9759 5.16057 17.6278 6.64011C18.2797 8.11965 18.4871 9.75688 18.2247 11.3522C17.9624 12.9476 17.2417 14.4322 16.1503 15.6251M10.0003 6.66675V13.3334M13.3337 10.0001H6.66699M2.08366 7.39591C1.81812 8.20313 1.6776 9.04621 1.66699 9.89592M2.35872 13.3334C2.82664 14.4099 3.51671 15.3755 4.38372 16.1667M3.86385 4.36252C4.09639 4.10938 4.34436 3.87087 4.60635 3.64836M7.20369 17.8501C9.28177 18.5904 11.5686 18.4765 13.5629 17.5334"
              stroke="currentColor"
              stroke-width="1.25"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          <span
            class="line-clamp-1 min-h-5 min-w-0 flex-1 overflow-hidden text-ellipsis text-sm font-normal leading-5
              text-[var(--neutral-800-main-text,#1F2937)] dark:text-neutral-100"
          >
            {{ t('showAll') }}
          </span>
        </button>
      </div>
    </div>
  </div>
</template>
